// src/lib/employeeStorage.ts
import { supabase } from './supabaseClient';

export interface EmployeeProfileData {
  phone: string;
  location: string;
  title: string;
  bio: string;
  education: string;
  specialization: string[];
  department: string;
  yearsExperience: number;
  startDate: string;
  imageUrl: string | null;
  analyst_type?: 'business' | 'problem_bank' | null;
}

// Get employee profile data from database
export const getEmployeeProfile = async (email: string, role: string): Promise<EmployeeProfileData> => {
  try {
    const { data, error } = await supabase
      .from('employee_profiles')
      .select('*')
      .eq('email', email)
      .single();
    
    if (!error && data) {
      return {
        phone: data.phone || '+1 (555) 000-0000',
        location: data.location || 'Remote',
        title: data.title || (role === 'senior_analyst' ? 'Senior Analyst' : 'Analyst'),
        bio: data.bio || 'Team member at Vett & Venture.',
        education: data.education || 'Bachelor\'s Degree',
        specialization: data.specialization || ['General'],
        department: data.department || (role === 'senior_analyst' ? 'Senior Management' : 'Investment Analysis'),
        yearsExperience: data.years_experience || 5,
        startDate: data.start_date || 'January 2024',
        imageUrl: data.image_url || null,
        analyst_type: data.analyst_type || null
      };
    }
  } catch (error) {
    console.error('Error loading employee profile from database:', error);
  }
  
  // Return default profile if not found in database
  return {
    phone: '+1 (555) 000-0000',
    location: 'Remote',
    title: role === 'senior_analyst' ? 'Senior Analyst' : 'Analyst',
    bio: 'Team member at Vett & Venture.',
    education: 'Bachelor\'s Degree',
    specialization: ['General'],
    department: role === 'senior_analyst' ? 'Senior Management' : 'Investment Analysis',
    yearsExperience: 5,
    startDate: 'January 2024',
    imageUrl: null,
    analyst_type: null
  };
};

// Save employee profile data to database
export const saveEmployeeProfile = async (email: string, data: Partial<EmployeeProfileData>): Promise<EmployeeProfileData> => {
  const existing = await getEmployeeProfile(email, 'analyst');
  const updated = { ...existing, ...data };
  
  try {
    const { error } = await supabase
      .from('employee_profiles')
      .upsert({
        email: email,
        phone: updated.phone,
        location: updated.location,
        title: updated.title,
        bio: updated.bio,
        education: updated.education,
        specialization: updated.specialization,
        department: updated.department,
        years_experience: updated.yearsExperience,
        start_date: updated.startDate,
        image_url: updated.imageUrl,
        analyst_type: updated.analyst_type,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'email'
      });
    
    if (error) {
      console.error('Error saving employee profile to database:', error);
    } else {
      console.log(`✅ Employee profile saved to database for ${email}`);
    }
  } catch (error) {
    console.error('Error saving employee profile to database:', error);
  }
  
  return updated;
};

// Get analyst type for a user from database
export const getAnalystType = async (email: string): Promise<'business' | 'problem_bank' | null> => {
  try {
    const { data, error } = await supabase
      .from('employee_profiles')
      .select('analyst_type')
      .eq('email', email)
      .single();
    
    if (!error && data && data.analyst_type) {
      return data.analyst_type;
    }
  } catch (error) {
    // Not found in database
  }
  
  return null;
};

// Create employee profile table if not exists
export const createEmployeeProfilesTable = async () => {
  const { error } = await supabase.rpc('create_employee_profiles_table');
  if (error) {
    console.error('Error creating employee_profiles table:', error);
  }
};